// HomeHeader.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './HomeHeader.css';

import scrollToTop from '../../utils/scrollToTop';

import logoGreen from '../../assets/images/header_logo_green.png';

// Public header nav. Blog removed 2026-05-09 (feature non-functional; /blog
// route still resolves but shows a "Coming soon" page). Contact Us removed
// 2026-05-09 (deferred to footer + FloatingContactButton; /contact route
// still resolves so existing backlinks and form submissions work).
const NAV_ITEMS = [
  { to: '/about', label: 'About Us' },
  { to: '/services', label: 'Services' },
  { to: '/prices', label: 'Prices' },
  { to: '/faqs', label: 'FAQS' },
  { to: '/portal/login', label: 'Client Portal' },
];

const HomeHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [headerClass, setHeaderClass] = useState('');
  const history = useHistory();
  const menuRef = useRef(null);
  const location = useLocation();

  const user = useSelector((state) => state.users.user);
  const isAdminUser = user?.isAdmin || false;
  const isEmployeeUser = user?.isEmployee || false;

  const handleNavClick = () => {
    scrollToTop();
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open);
  };

  const getScrollbarWidth = () => {
    return window.innerWidth - document.documentElement.clientWidth;
  };

  useEffect(() => {
    if (isMenuOpen) {
      const scrollbarWidth = getScrollbarWidth();
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.paddingRight = '0px';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.body.style.paddingRight = '0px';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (
      location.pathname === '/' ||
      location.pathname === '/jobs' ||
      location.pathname === '/faqs'
    ) {
      setHeaderClass('');
    } else {
      setHeaderClass('not-home');
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
      document.body.style.overflow = 'auto';
      document.body.style.paddingRight = '0px';
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  useEffect(() => {
    const body = document.body;
    if (isMenuOpen) {
      body.classList.add('menu-open');
    } else {
      body.classList.remove('menu-open');
    }
    return () => body.classList.remove('menu-open');
  }, [isMenuOpen]);

  return (
    <header className={`homeheader-header ${headerClass}`} ref={menuRef}>
      {/* Logo — real anchor so middle/right-click and "open in new tab" work */}
      <Link
        to="/"
        className="logo flex center"
        onClick={handleNavClick}
        aria-label="The Dog Lodge — home"
      >
        <img
          src={logoGreen}
          alt="The Dog Lodge"
          className="logo-image"
          width="280"
          height="103"
          decoding="async"
        />
      </Link>

      {/* Primary navigation. NavLink applies aria-current="page" automatically. */}
      <nav
        id="primary-nav"
        aria-label="Primary"
        className={`nav-links ${isMenuOpen ? 'open' : ''}`}
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            exact
            activeClassName="is-active"
            aria-current={location.pathname === item.to ? 'page' : undefined}
            onClick={handleNavClick}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Admin/Employee Portal Buttons */}
      <div className="portal-buttons">
        {isAdminUser && (
          <button
            type="button"
            className="portal-button admin-portal"
            onClick={() => history.push('/secure')}
            title="Go to Admin Portal"
          >
            Admin Portal
          </button>
        )}
        {isEmployeeUser && !isAdminUser && (
          <button
            type="button"
            className="portal-button employee-portal"
            onClick={() => history.push('/employee')}
            title="Go to Employee Portal"
          >
            Employee Portal
          </button>
        )}
      </div>

      {/* Mobile menu toggle */}
      <button
        type="button"
        className={`menu-button ${isMenuOpen ? 'open' : ''}`}
        onClick={toggleMenu}
        aria-expanded={isMenuOpen}
        aria-controls="primary-nav"
        aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
      >
        <span className="menu-bar" aria-hidden="true"></span>
        <span className="menu-bar" aria-hidden="true"></span>
        <span className="menu-bar" aria-hidden="true"></span>
      </button>
    </header>
  );
};

export default HomeHeader;
